/**
 * API Client -- Axios instance with auth token management, retry logic,
 * and error normalization for all backend HTTP calls.
 *
 * Features:
 *   - Single Axios instance with baseURL from config/api.ts
 *   - Request interceptor: attaches Bearer token from localStorage
 *   - Response interceptor: handles 401 (token refresh), 403, 5xx with retry
 *   - Error normalization: all HTTP errors are wrapped in a consistent shape
 *   - Perspective mode support: admin viewing as customer (X-Perspective-User header)
 *   - Error tracking: reports Axios errors to errorTrackingService
 *
 * Auth token lifecycle:
 *   Login -> localStorage.setItem('accessToken', token)
 *   Each request -> interceptor reads from localStorage, attaches as Bearer header
 *   401 response -> interceptor attempts refresh via /auth/refresh-token
 *   Refresh fails -> redirect to /login
 */
import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL } from '../config/api';
// ── Inlined from perspectiveBootstrap.ts to avoid pulling 10KB into the main bundle ──
// perspectiveBootstrap is still dynamically imported by App.tsx for full initialization;
// this inlined check is just the lightweight guard used in request interceptors.
const isPerspectiveMode = (): boolean => {
  try {
    return window.self !== window.top && sessionStorage.getItem('perspective_mode') === 'true';
  } catch { return false; }
};
// ── Lazy-loaded: error reporting is fire-and-forget ──
const reportAxiosError = (method: string, url: string, status: number | null, responseData: unknown) =>
  import('./errorTrackingService').then(m => m.reportAxiosError(method, url, status, responseData));

// Re-export for backward compatibility
export { API_BASE_URL };
export const getApiBaseUrl = (): string => API_BASE_URL;

const API_TIMEOUT = 30000;

// =============================================================================
// Tab-Local Token Store
// =============================================================================
// CRITICAL: These module-scoped variables are the ONLY source of truth for the
// request interceptor. They are isolated per tab (each tab runs its own JS
// context). localStorage is used only for persistence across page reloads —
// never read mid-session for API calls.
//
// WHY: localStorage is shared across all tabs on the same origin. If two users
// (or the same user twice) open separate tabs and log in, one tab's token write
// overwrites the other's. Without this guard, Tab A's next API call silently
// sends Tab B's token, causing 403s and cross-user data leaks.
//
// The in-memory variables break that coupling: each tab holds onto the token it
// authenticated with, regardless of what other tabs write to localStorage.
// ---------------------------------------------------------------------------
let _accessToken:  string | null = localStorage.getItem('access_token');
let _refreshToken: string | null = localStorage.getItem('refresh_token');

/** Update both the in-memory token and localStorage (write-through). */
export function setAccessToken(token: string | null): void {
    _accessToken = token;
    if (token) {
        localStorage.setItem('access_token', token);
    } else {
        localStorage.removeItem('access_token');
    }
}

/** Update both the in-memory refresh token and localStorage (write-through). */
export function setRefreshToken(token: string | null): void {
    _refreshToken = token;
    if (token) {
        localStorage.setItem('refresh_token', token);
    } else {
        localStorage.removeItem('refresh_token');
    }
}

/** Read the tab-local access token (never touches localStorage). */
export function getAccessToken(): string | null {
    return _accessToken;
}

/** Clear both tokens from memory and localStorage (used on logout). */
export function clearTokens(): void {
    _accessToken = null;
    _refreshToken = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('auth-storage');
}

// =============================================================================
// Delegation Grant ID (for X-Delegation-Grant-Id header)
// =============================================================================
// Module-scoped: set by DelegationStore when entering delegation mode,
// cleared on exit. The request interceptor reads this on every call.
// ---------------------------------------------------------------------------
let _activeDelegationGrantId: string | null = null;

// sessionStorage key shared with ApplicationLayout / delegationStore.
// The helper id is persisted alongside the grant id once a grant is resolved,
// so the request interceptor can verify the grant belongs to the CURRENT user
// before attaching the delegation header. This prevents a leftover grant from a
// previous session (same browser tab) poisoning the next user's own requests.
const DELEGATION_HELPER_ID_KEY = 'delegation_helperId';

/** Set the active delegation grant ID (header injected on all subsequent requests). */
export function setActiveDelegationGrantId(grantId: string | null): void {
    const prev = _activeDelegationGrantId;
    _activeDelegationGrantId = grantId;
    if (grantId !== prev) {
        console.log(`[DELEG-DIAG] setActiveDelegationGrantId: ${prev} → ${grantId}`,
            new Error().stack?.split('\n').slice(1, 4).map(s => s.trim()).join(' < '));
    }
}

/** Read the active delegation grant ID. */
export function getActiveDelegationGrantId(): string | null {
    return _activeDelegationGrantId;
}

/**
 * Fail-safe ownership guard for the delegation header.
 *
 * Returns true (attach the header) UNLESS we can prove the grant belongs to a
 * DIFFERENT user than the one currently authenticated. We only block on a
 * *certain* mismatch — when both the grant's helper id (persisted in
 * sessionStorage during the helper's session) and the current user id (from the
 * persisted auth-storage) are known and differ. In every ambiguous case we
 * attach, so the legitimate helper flow is never broken; the guard can only
 * prevent the cross-session leak, never introduce a regression.
 */
function delegationGrantMatchesCurrentUser(): boolean {
    try {
        const helperId = sessionStorage.getItem(DELEGATION_HELPER_ID_KEY);
        if (!helperId) return true; // helper id unknown — fail-safe: attach

        const raw = localStorage.getItem('auth-storage');
        if (!raw) return true; // current user unknown — fail-safe: attach
        const currentUserId = JSON.parse(raw)?.state?.user?.id ?? null;
        if (!currentUserId) return true; // fail-safe: attach

        return helperId === currentUserId;
    } catch {
        return true; // never block on a parsing/storage error
    }
}

// =============================================================================
// Axios Instance - SINGLE baseURL from config/api.ts
// =============================================================================
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// =============================================================================
// Token Refresh Synchronization
// =============================================================================
let isRefreshing = false;
interface RefreshSubscriber {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}
let refreshSubscribers: RefreshSubscriber[] = [];
let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function subscribeTokenRefresh(resolve: (token: string) => void, reject: (err: Error) => void) {
  refreshSubscribers.push({ resolve, reject });
}

function onRefreshComplete(newToken: string) {
  refreshSubscribers.forEach(sub => sub.resolve(newToken));
  refreshSubscribers = [];
}

function onRefreshFailed(err: Error) {
  refreshSubscribers.forEach(sub => sub.reject(err));
  refreshSubscribers = [];
}

// =============================================================================
// JWT Utilities
// =============================================================================

/**
 * Decode the exp claim from a JWT without verifying the signature.
 * Returns the expiry as a Unix timestamp (seconds), or null if parsing fails.
 */
function getJwtExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Returns true if the given access token expires within `thresholdSeconds` from now.
 */
function isTokenExpiringSoon(token: string, thresholdSeconds = 120): boolean {
  const exp = getJwtExpiry(token);
  if (exp === null) return false;
  return exp - Math.floor(Date.now() / 1000) < thresholdSeconds;
}

// =============================================================================
// Login Redirect Helper
// =============================================================================

/**
 * Determine the correct login page for the current user based on their persisted role.
 * Admin/Manager/Reviewer users are redirected to /admin/login; Customers to /customer/login.
 */
function getLoginRedirectUrl(): string {
  try {
    const authRaw = localStorage.getItem('auth-storage');
    if (authRaw) {
      const authData = JSON.parse(authRaw);
      const role: string | undefined = authData?.state?.user?.role;
      if (role && ['ADMIN', 'MANAGER', 'REVIEWER'].includes(role)) {
        return '/admin/login';
      }
    }
  } catch {
    // Ignore parse errors — fall back to customer login
  }
  return '/customer/login';
}

// =============================================================================
// Core Token Refresh Logic
// =============================================================================

/**
 * Execute a token refresh against the backend. Stores the new token pair in
 * localStorage and returns the new access token. Throws on failure.
 */
async function doTokenRefresh(): Promise<string> {
  // Read from tab-local memory, NOT localStorage
  if (!_refreshToken || _refreshToken === 'undefined' || _refreshToken === 'null') {
    throw new Error('No valid refresh token available');
  }

  const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
    refresh_token: _refreshToken,
  });

  const { accessToken, refreshToken: newRefreshToken } = response.data.data;

  if (!accessToken || !newRefreshToken) {
    throw new Error('Invalid token response from server');
  }

  // Write-through: update memory AND localStorage
  setAccessToken(accessToken);
  setRefreshToken(newRefreshToken);

  return accessToken;
}

// =============================================================================
// Proactive Token Refresh Scheduler
// =============================================================================

/**
 * Schedule a proactive token refresh to fire 2 minutes before the access token expires.
 * Cancel and reschedule whenever new tokens are issued. This keeps the user logged in
 * indefinitely as long as they have a valid refresh token — no idle-logout.
 *
 * Call this after every successful login or token refresh.
 */
export function scheduleProactiveTokenRefresh(accessToken: string): void {
  if (proactiveRefreshTimer !== null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }

  const exp = getJwtExpiry(accessToken);
  if (exp === null) return;

  const nowSeconds = Math.floor(Date.now() / 1000);
  const secondsUntilExpiry = exp - nowSeconds;
  // Refresh 2 minutes before expiry; clamp to at least 5 seconds to avoid tight loops.
  const refreshInMs = Math.max((secondsUntilExpiry - 120) * 1000, 5000);

  proactiveRefreshTimer = setTimeout(async () => {
    proactiveRefreshTimer = null;

    if (isPerspectiveMode()) return; // Perspective sessions have no refresh token
    if (isRefreshing) return;        // A concurrent reactive refresh is already running

    isRefreshing = true;
    try {
      const newAccessToken = await doTokenRefresh();
      isRefreshing = false;
      onRefreshComplete(newAccessToken);
      scheduleProactiveTokenRefresh(newAccessToken); // Chain the next refresh
      console.debug('[Auth] Proactive token refresh succeeded');
    } catch (err) {
      isRefreshing = false;
      onRefreshFailed(err instanceof Error ? err : new Error('Proactive token refresh failed'));
      // Do NOT force-redirect here. The user may be idle; wait for their next request
      // so the reactive 401 handler can cleanly redirect them to the right login page.
      console.warn('[Auth] Proactive token refresh failed — next request will trigger re-login', err);
    }
  }, refreshInMs);
}

// =============================================================================
// Visibility-Change Re-Arm
// =============================================================================
// When a browser tab is hidden, setTimeout calls are heavily throttled
// (sometimes clamped to 1-second intervals or paused entirely on mobile).
// This means the proactive timer above may not fire before the 15-minute
// access token expires. Re-arming it every time the tab becomes visible
// again ensures we immediately catch up and refresh before the first
// request after the user returns.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  if (isPerspectiveMode()) return;

  // Don't re-arm proactive refresh inside iframes (UI Page Tests, embeds).
  // Only the main window should drive token refresh scheduling.
  try { if (window.self !== window.top) return; } catch { return; }

  // Read from tab-local memory, NOT localStorage
  if (!_accessToken || _accessToken === 'undefined' || _accessToken === 'null') return;

  // Re-arm (or fire immediately if already expired) so the proactive refresh
  // fires within 5 seconds of the user returning to the tab.
  scheduleProactiveTokenRefresh(_accessToken);
  console.debug('[Auth] Proactive token refresh re-armed on tab visibility');
});

// =============================================================================
// Request Interceptor
// =============================================================================
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // ── Token selection ──
    // isPerspectiveMode() returns true ONLY inside an iframe with an active
    // perspective session. In the parent window it always returns false,
    // even if the iframe has leaked perspective_* keys into sessionStorage.
    // This prevents the admin panel from accidentally using the customer's
    // perspective token for its own API calls (which caused 403 on admin endpoints).
    const inPerspective = isPerspectiveMode();
    const token = inPerspective
      ? sessionStorage.getItem('perspective_token')
      : _accessToken;  // Tab-local memory — NOT localStorage

    // ── Just-in-time proactive refresh ──
    // If the token is within 2 minutes of expiry, refresh it now before sending
    // the request. This is a belt-and-suspenders safety net for cases where the
    // scheduled proactive refresh didn't fire (e.g. tab was backgrounded/throttled).
    // Skip in iframe contexts (UI Page Tests, perspective) — only the main window
    // should perform token refreshes to avoid racing with the parent's refresh cycle.
    const isIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
    if (
      !inPerspective &&
      !isIframe &&
      token &&
      !isRefreshing &&
      !config.url?.includes('/auth/login') &&
      !config.url?.includes('/auth/refresh') &&
      isTokenExpiringSoon(token, 120)
    ) {
      isRefreshing = true;
      try {
        const newToken = await doTokenRefresh();
        isRefreshing = false;
        onRefreshComplete(newToken);
        scheduleProactiveTokenRefresh(newToken);
        if (config.headers) {
          config.headers['Authorization'] = `Bearer ${newToken}`;
        }
        return config;
      } catch (err) {
        // Let the request proceed with the expiring token; the 401 handler will take over.
        isRefreshing = false;
        onRefreshFailed(err instanceof Error ? err : new Error('JIT token refresh failed'));
      }
    }

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add perspective header for backend observability (iframe only)
    if (inPerspective && config.headers) {
      config.headers['X-Perspective-Mode'] = 'true';
    }

    // Add language preference
    const preferredLanguage = localStorage.getItem('preferred_language');
    if (preferredLanguage && config.headers) {
      config.headers['Accept-Language'] = preferredLanguage;
      config.headers['X-User-Language'] = preferredLanguage;
    }

    // Add delegation grant ID header (when acting on behalf of an applicant).
    // Guard: never send the header for a grant that belongs to a different user
    // than the one currently signed in (prevents cross-session grant leakage).
    if (_activeDelegationGrantId && config.headers && delegationGrantMatchesCurrentUser()) {
      config.headers['X-Delegation-Grant-Id'] = _activeDelegationGrantId;
      if (config.method && ['put', 'post', 'patch', 'delete'].includes(config.method.toLowerCase())) {
        console.log(`[DELEG-DIAG] ✅ Header ATTACHED: ${config.method?.toUpperCase()} ${config.url}  grantId=${_activeDelegationGrantId}`);
      }
    } else if (config.method && ['put', 'post', 'patch', 'delete'].includes(config.method.toLowerCase())) {
      console.warn(`[DELEG-DIAG] ❌ Header NOT attached: ${config.method?.toUpperCase()} ${config.url}  grantId=${_activeDelegationGrantId}  matchesUser=${_activeDelegationGrantId ? delegationGrantMatchesCurrentUser() : 'n/a'}`);
    }

    // Add debug header if cookie is present
    const debugCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('X-Debug-Token='));
    if (debugCookie && config.headers) {
      config.headers['X-Debug-Token'] = debugCookie.split('=')[1];
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// =============================================================================
// Response Interceptor
// =============================================================================
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    // Handle 401 Unauthorized - Token expired
    if (error.response?.status === 401) {
      const originalRequest = error.config;

      // Don't refresh for login/refresh endpoints
      if (originalRequest?.url?.includes('/auth/login') || 
          originalRequest?.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      // Don't attempt refresh in perspective mode (no refresh token exists).
      // isPerspectiveMode() is iframe-gated so this only triggers inside
      // the perspective iframe — never in the admin parent window.
      if (isPerspectiveMode()) {
        console.warn('[PERSPECTIVE] Token expired — no refresh available');
        return Promise.reject(error);
      }

      // Don't clear tokens or redirect from ANY iframe context (UI Page Tests,
      // embedded previews, etc.). Only the main window should manage auth state.
      // Without this guard, a 401 inside a UI test iframe clears localStorage
      // (shared across same-origin contexts) and expels the admin from the panel.
      try {
        if (window.self !== window.top) {
          console.warn('[Auth] 401 in iframe — suppressing token clear and redirect');
          return Promise.reject(error);
        }
      } catch {
        // Cross-origin iframe — also suppress (can't access parent anyway)
        return Promise.reject(error);
      }

      const refreshToken = _refreshToken;
      
      if (!refreshToken || refreshToken === 'undefined' || refreshToken === 'null') {
        clearTokens();
        window.location.href = getLoginRedirectUrl();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (newToken: string) => {
              if (originalRequest?.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }
              resolve(apiClient(originalRequest!));
            },
            (err: Error) => reject(err),
          );
        });
      }

      isRefreshing = true;

      try {
        const newAccessToken = await doTokenRefresh();

        isRefreshing = false;
        onRefreshComplete(newAccessToken);
        scheduleProactiveTokenRefresh(newAccessToken);

        if (originalRequest?.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        }
        return apiClient(originalRequest!);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError instanceof Error ? refreshError : new Error('Reactive token refresh failed'));
        
        clearTokens();
        window.location.href = getLoginRedirectUrl();
        return Promise.reject(refreshError);
      }
    }

    // ── Report non-401 errors (5xx, network failures) to the monitoring pipeline ──
    // 401s are handled above (token refresh / redirect). Everything else — including
    // 5xx crashes and network timeouts — is reported here so the backend can alert.
    const axiosErr = error as AxiosError;
    const status = axiosErr.response?.status ?? null;
    const method = axiosErr.config?.method?.toUpperCase() ?? 'UNKNOWN';
    const url = axiosErr.config?.url ?? 'unknown';
    const responseData = axiosErr.response?.data as Record<string, unknown> | null ?? null;

    // If the backend already tracked this error (error.reported === true), skip frontend
    // reporting to avoid a duplicate alert email for the same crash event.
    // The backend sets reported:true in GlobalExceptionMapper / CatchAllExceptionMapper
    // after ExceptionTrackingService.trackException() completes successfully.
    // If reported is absent or false (network error, proxy 502, or tracking failed),
    // we fall through and report from the frontend as the only available source.
    const alreadyReported =
      responseData !== null &&
      typeof responseData.error === 'object' &&
      responseData.error !== null &&
      (responseData.error as Record<string, unknown>).reported === true;

    if (!alreadyReported) {
      reportAxiosError(method, url, status, responseData);
    }

    return Promise.reject(error);
  }
);

// =============================================================================
// Helper Functions
// =============================================================================
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: { message?: string; code?: string } }>;
    return axiosError.response?.data?.error?.message || axiosError.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
};

/**
 * Known backend error codes → i18n keys.
 * When a code is matched, the localized string is returned instead of the
 * raw English message from the API.  Falls back to getErrorMessage().
 */
const ERROR_CODE_I18N: Record<string, string> = {
  INVALID_TOKEN:        'auth.verifyEmail.errorInvalidToken',
  RATE_LIMIT_EXCEEDED:  'errors.rateLimitExceeded',
  EMAIL_NOT_VERIFIED:   'errors.emailNotVerified',
  ACCOUNT_LOCKED:       'errors.accountLocked',
  ACCOUNT_DEACTIVATED:  'errors.accountDeactivated',
};

export const getLocalizedErrorMessage = (
  error: unknown,
  t: (key: string) => string,
): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: { message?: string; code?: string } }>;
    const code = axiosError.response?.data?.error?.code;
    if (code && ERROR_CODE_I18N[code]) {
      const localized = t(ERROR_CODE_I18N[code]);
      // t() returns the key itself when no translation is found — fall back
      // to the English message in that case.
      if (localized && localized !== ERROR_CODE_I18N[code]) {
        return localized;
      }
    }
  }
  return getErrorMessage(error);
};

export const isErrorCode = (error: unknown, code: string): boolean => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error?: { code?: string } }>;
    return axiosError.response?.data?.error?.code === code;
  }
  return false;
};

export default apiClient;